import { t } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BriefcaseBusiness, CalendarCheck2, Check, FileCheck2, LoaderCircle, Route, ShieldCheck, UserRoundCheck, UsersRound, ArrowRight } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useSearch } from "wouter";
import type { z } from "zod";
import { loginSchema } from "@shared/schemas";
import { TruckMark } from "@/components/app-shell";
import { api, jsonBody } from "@/lib/api";
import { PasswordInput } from "@/components/ui/password-input";
import { redirectAfterLogin } from "@/lib/utils";
type LoginInput = z.infer<typeof loginSchema>;

function LoginVisual() {
    return <section className="auth-visual" aria-label={t("spa_driverflow_capabilities")}>
      <div className="auth-visual-grid" aria-hidden="true" />
      <div className="auth-visual-glow auth-visual-glow-top" aria-hidden="true" />
      <div className="auth-visual-glow auth-visual-glow-bottom" aria-hidden="true" />
      <div className="auth-visual-inner">
        <div className="visual-scene" aria-hidden="true">
          <div className="visual-dashboard">
            <header className="visual-dashboard-header">
              <div className="visual-dashboard-title">
                <span className="visual-dashboard-icon"><Route /></span>
                <span><strong>{t("login_visual_pipeline")}</strong><small>{t("login_visual_workspace")}</small></span>
              </div>
              <span className="visual-live"><i />{t("login_visual_live")}</span>
            </header>

            <div className="visual-journey">
              <span className="visual-journey-line" />
              <div className="visual-stage complete"><span><UsersRound /></span><small>{t("login_visual_stage_new")}</small></div>
              <div className="visual-stage complete"><span><ShieldCheck /></span><small>{t("login_visual_stage_qualified")}</small></div>
              <div className="visual-stage active"><span><CalendarCheck2 /></span><small>{t("login_visual_stage_interview")}</small></div>
              <div className="visual-stage"><span><BriefcaseBusiness /></span><small>{t("login_visual_stage_hired")}</small></div>
            </div>

            <div className="visual-dashboard-grid">
              <section className="visual-candidate-panel">
                <header><strong>{t("login_visual_candidates")}</strong><span className="visual-avatars"><i>AC</i><i>MR</i><i>JL</i></span></header>
                <div className="visual-candidate-row">
                  <span className="visual-avatar visual-avatar-teal">AC</span>
                  <span className="visual-candidate-info"><b /><i /></span>
                  <span className="visual-status ready"><Check />{t("login_visual_candidate_ready")}</span>
                </div>
                <div className="visual-candidate-row">
                  <span className="visual-avatar visual-avatar-sand">MR</span>
                  <span className="visual-candidate-info"><b /><i /></span>
                  <span className="visual-status review">{t("login_visual_candidate_review")}</span>
                </div>
              </section>

              <section className="visual-activity-panel">
                <strong>{t("login_visual_activity")}</strong>
                <div><span className="visual-activity-icon success"><FileCheck2 /></span><p>{t("login_visual_documents_verified")}<small>{t("login_visual_compliance_clear")}</small></p></div>
                <div><span className="visual-activity-icon calendar"><CalendarCheck2 /></span><p>{t("login_visual_interview_scheduled")}<small>{t("login_visual_updated_now")}</small></p></div>
              </section>
            </div>
          </div>

          <div className="visual-float-card visual-float-doc">
            <span><FileCheck2 /></span><p><strong>{t("login_visual_documents_verified")}</strong><small>{t("login_visual_compliance_clear")}</small></p><Check className="visual-float-check" />
          </div>
          <div className="visual-float-card visual-float-hire">
            <span><UserRoundCheck /></span><p><strong>{t("login_visual_candidate_ready")}</strong><small>{t("login_visual_updated_now")}</small></p>
          </div>
        </div>

        <div className="visual-copy"><span className="visual-eyebrow">BUILT FOR THE PEOPLE WHO KEEP US MOVING</span><h2>{t("login_marketing_heading")}</h2><p>{t("login_marketing_description")}</p></div>
      </div>
    </section>;
}

const isDevBuild = import.meta.env.DEV;

export function LoginPage() {
    const [, navigate] = useLocation();
    const search = useSearch();
    const queryClient = useQueryClient();
    // Demo credentials must never ship pre-filled outside development — the
    // block below renders real admin access on the production sign-in card.
    const demo = isDevBuild ? { email: "admin@roadstar.local", password: "Admin123!" } : undefined;
    const [capsLock, setCapsLock] = useState(false);
    const form = useForm<LoginInput>({
        resolver: zodResolver(loginSchema),
        defaultValues: demo,
    });
    const login = useMutation({
        mutationFn: (value: LoginInput) => api("/api/auth/login", { method: "POST", ...jsonBody(value) }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["session"] });
            // Return to the deep link that triggered the sign-in instead of
            // always dumping people on the dashboard.
            navigate(redirectAfterLogin(search));
        },
    });
    useEffect(() => {
        document.body.classList.add("auth-body");
        return () => document.body.classList.remove("auth-body");
    }, []);
    return <main className="auth-layout">
    <section className="auth-panel">
      <div className="auth-card">
        <a className="brand auth-brand" href="/" aria-label={t("spa_driverflow_home")}><TruckMark /><span><strong>DriverFlow</strong><small>{t("brand_subtitle")}</small></span></a>
        <div className="auth-copy"><span className="auth-eyebrow">YOUR NEXT GREAT HIRE STARTS HERE</span><h1>{t("login_welcome")}</h1><p>{t("login_description")}</p></div>
        {login.error ? <div className="alert danger" role="alert">{login.error.message}</div> : null}
        <form className="stack-form" onSubmit={form.handleSubmit((value) => login.mutate(value))} noValidate>
          <label>{t("login_email_label")}<input type="email" autoComplete="username" autoFocus placeholder="you@company.com" aria-invalid={Boolean(form.formState.errors.email)} {...form.register("email")}/>
            {form.formState.errors.email ? <span className="field-error">{t(form.formState.errors.email.message ?? "")}</span> : null}
          </label>
          <label>{t("login_password_label")}
            <PasswordInput autoComplete="current-password" placeholder="••••••••" aria-invalid={Boolean(form.formState.errors.password)} onKeyUp={(event) => setCapsLock(event.getModifierState?.("CapsLock") ?? false)} {...form.register("password")}/>
            {capsLock ? <span className="field-error" role="status">⇪ {t("auth_caps_lock")}</span> : null}
            {form.formState.errors.password ? <span className="field-error">{t(form.formState.errors.password.message ?? "")}</span> : null}
          </label>
          <button className="primary-button large" type="submit" disabled={login.isPending}>{login.isPending ? <><LoaderCircle className="ui-icon spinner" aria-hidden="true"/>{t("spa_signing_in")}</> : <>{t("btn_sign_in")}<ArrowRight className="ui-icon"/></>}</button>
          <Link className="text-link center" href="/forgot-password">{t("login_forgot_password")}</Link>
        </form>
        {demo ? <div className="demo-credentials"><strong>{t("login_demo_access")}</strong><code>{demo.email}</code><code>{demo.password}</code></div> : null}
      </div>
    </section>
    <LoginVisual />
  </main>;
}
